import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  buildModelToggles,
  buildModifyDetailsPayload,
  formatEffectiveFromDisplay,
  mapEditHistoryFromApi,
  mapMeterRatesFromApi,
  mapModelsFromApi,
  mapPlansFromApi,
  mapTrialPlanFromApi,
  mapUnlimitedPlanFromApi,
  unwrapApiData,
} from './pricingTransforms';
import {
  getPricingEditHistory,
  getPricingMeterRates,
  getPricingModels,
  getPricingPlans,
  modifyPricingDetails,
  getPricingCreditBalance,
} from '../../requests';
import { isUnauthorizedError, logoutOnUnauthorized, notifyError } from '../../utils/helper';
import { DEFAULT_PUBLISH_CONFIG, parseCreditNumber, parseEuroNumber } from './mockData';

// Which published version the rate card shows (null ids = base defaults), plus the
// versions that are published but not in effect yet.
function deriveRateCardVersion(plans, ratesEditId, historyData) {
  const versionPlan = plans.find((plan) => plan.editsId != null) || null;
  const scheduled = (Array.isArray(historyData) ? historyData : [])
    .filter((row) => row.status === 'Scheduled')
    .sort((a, b) => new Date(a.effective_from) - new Date(b.effective_from))
    .map((row) => ({
      version: row.version || `v${row.id}`,
      date: formatEffectiveFromDisplay(row.effective_from),
    }));

  return {
    plansEditId: versionPlan?.editsId ?? null,
    plansEffectiveFrom: versionPlan?.effectiveFrom ?? null,
    ratesEditId: ratesEditId ?? null,
    scheduled,
  };
}

const PricingContext = createContext(null);

export function PricingProvider({ children }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [planTiers, setPlanTiers] = useState([]);
  const [trialPlan, setTrialPlan] = useState(null);
  const [unlimitedPlan, setUnlimitedPlan] = useState(null);
  const [changeHistory, setChangeHistory] = useState([]);
  const [models, setModels] = useState([]);
  const [modelToggles, setModelToggles] = useState({});
  const [meterRateRows, setMeterRateRows] = useState([]);
  const [meterRates, setMeterRates] = useState({});
  const [effectiveFrom, setEffectiveFrom] = useState(() => formatEffectiveFromDisplay());
  const [publishScope, setPublishScope] = useState('New Contract');
  const [publishConfig, setPublishConfig] = useState(DEFAULT_PUBLISH_CONFIG);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [creditBalance, setCreditBalance] = useState(null);
  const [creditBalanceLoading, setCreditBalanceLoading] = useState(false);
  const [rateCardVersion, setRateCardVersion] = useState(null);

  const applyRateCardData = useCallback((plansData, modelsData, ratesData, historyData) => {
    const nextPlans = mapPlansFromApi(plansData);
    const nextModels = mapModelsFromApi(modelsData);
    const { rows, values, editsId: ratesEditId } = mapMeterRatesFromApi(ratesData);

    setRateCardVersion(deriveRateCardVersion(nextPlans, ratesEditId, historyData));
    setPlanTiers(nextPlans);
    setTrialPlan(mapTrialPlanFromApi(plansData));
    setUnlimitedPlan(mapUnlimitedPlanFromApi(plansData));
    setModels(nextModels);
    setModelToggles(buildModelToggles(nextModels, nextPlans));
    setMeterRateRows(rows);
    setMeterRates(values);
    setChangeHistory(mapEditHistoryFromApi(historyData));
  }, []);

  const loadRateCard = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);

      const responses = await Promise.all([
        getPricingPlans(),
        getPricingModels(),
        getPricingMeterRates(),
        getPricingEditHistory(),
      ]);

      const unauthorizedResponse = responses.find((res) =>
        isUnauthorizedError({ response: res })
      );
      if (unauthorizedResponse) {
        logoutOnUnauthorized({ response: unauthorizedResponse }, dispatch, navigate);
        return;
      }

      const [plansRes, modelsRes, ratesRes, historyRes] = responses;

      applyRateCardData(
        unwrapApiData(plansRes),
        unwrapApiData(modelsRes),
        unwrapApiData(ratesRes),
        unwrapApiData(historyRes)
      );
    } catch (err) {
      if (logoutOnUnauthorized(err, dispatch, navigate)) return;
      const message = err?.response?.data?.message || 'Failed to load rate card data';
      setLoadError(message);
      notifyError(message);
    } finally {
      setLoading(false);
    }
  }, [applyRateCardData, dispatch, navigate]);

  const parseCreditBalance = (res) => {
    const payload = unwrapApiData(res);
    if (!payload) return null;
    return (
      payload.balance ??
      payload.credit_balance ??
      payload.credits ??
      payload.creditBalance ??
      payload.available_credits ??
      payload.availableCredits ??
      null
    );
  };

  const refreshCreditBalance = useCallback(async () => {
    try {
      setCreditBalanceLoading(true);
      const res = await getPricingCreditBalance();
      if (res && res.status < 400) {
        setCreditBalance(parseCreditBalance(res));
      }
    } catch (err) {
      // ignore credit balance failures
    } finally {
      setCreditBalanceLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRateCard();
    refreshCreditBalance();
  }, [loadRateCard, refreshCreditBalance]);

  const publish = useCallback(
    async (comment = '') => {
      try {
        setPublishing(true);

        if (!publishConfig?.length) {
          notifyError('Select at least one config section to publish');
          return false;
        }

        // Catches empty fields and English-style "1,500", which parseEuroNumber reads as 1.5.
        const invalidTier = (planTiers || []).find(
          (tier) =>
            parseEuroNumber(tier.packageMo) < 100 || parseCreditNumber(tier.includedCredits) <= 0
        );
        if (invalidTier) {
          notifyError(
            `Tier ${invalidTier.id}: the package price must be at least €100 (use a dot as thousands separator) and the included credits above 0`
          );
          return false;
        }

        const payload = buildModifyDetailsPayload({
          description: comment,
          effectiveFrom,
          effectiveFor: publishScope,
          publishConfig,
          planTiers,
          modelToggles,
          models,
          meterRateRows,
          meterRates,
        });

        const res = await modifyPricingDetails(payload);
        if (logoutOnUnauthorized({ response: res }, dispatch, navigate)) return false;
        if (res?.status >= 400) {
          throw new Error(res?.data?.message || 'Failed to publish rate card');
        }

        await loadRateCard();
        setPublishModalOpen(false);
        return true;
      } catch (err) {
        if (logoutOnUnauthorized(err, dispatch, navigate)) return false;
        const message =
          err?.response?.data?.message || err?.message || 'Failed to publish rate card';
        notifyError(message);
        return false;
      } finally {
        setPublishing(false);
      }
    },
    [
      effectiveFrom,
      publishScope,
      publishConfig,
      planTiers,
      modelToggles,
      models,
      meterRateRows,
      meterRates,
      loadRateCard,
      dispatch,
      navigate,
    ]
  );

  const value = useMemo(
    () => ({
      planTiers,
      setPlanTiers,
      trialPlan,
      unlimitedPlan,
      changeHistory,
      models,
      modelToggles,
      setModelToggles,
      meterRateRows,
      meterRates,
      setMeterRates,
      effectiveFrom,
      setEffectiveFrom,
      publishScope,
      setPublishScope,
      publishConfig,
      setPublishConfig,
      publishModalOpen,
      setPublishModalOpen,
      publish,
      loading,
      publishing,
      loadError,
      reloadRateCard: loadRateCard,
      rateCardVersion,
      creditBalance,
      creditBalanceLoading,
      refreshCreditBalance,
    }),
    [
      rateCardVersion,
      planTiers,
      trialPlan,
      unlimitedPlan,
      changeHistory,
      models,
      modelToggles,
      meterRateRows,
      meterRates,
      effectiveFrom,
      publishScope,
      publishConfig,
      publishModalOpen,
      loading,
      publishing,
      loadError,
      loadRateCard,
      publish,
      creditBalance,
      creditBalanceLoading,
      refreshCreditBalance,
    ]
  );

  return <PricingContext.Provider value={value}>{children}</PricingContext.Provider>;
}

export function usePricing() {
  const ctx = useContext(PricingContext);
  if (!ctx) throw new Error('usePricing must be used within PricingProvider');
  return ctx;
}
